import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';

import { Context } from 'store';
import { AsyncTable } from '_src/components';
import { PageContainer } from '_src/shared/styles';
import { PersistentRepo } from '_src/shared/repo';
import { Select, Input } from '_src/shared/form-helpers';
import { Card, Button } from 'antd';
import services from '_src/services';
import { debounce } from '_src/utils';
import download from 'downloadjs';
import { camelToSnakeCase } from '../../utils/helpers';

import { FilterRow } from './styles';

const LOCATION_FILTERS = [
  { id: 0, label: 'Both' },
  { id: 1, label: 'Yard' },
  { id: 2, label: 'Classroom' }
];

const stylesTablet = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  ? { marginTop: 30 }
  : { marginTop: 0 };
const baseApi = GLOBALS.BASE_API;

export default () => {
  const { getData } = useContext(Context);
  const token = PersistentRepo.get('token');

  const resource = 'studentIntervention';
  const config = services[resource];
  const attendanceConfig = services.attendance;

  const allBranchesLocation = PersistentRepo.get('allBranches');

  const getInitialLocation = () => {
    if (allBranchesLocation) {
      return { selectedBranch: allBranchesLocation, selectedFilterLocation: 0 };
    }
    const location = PersistentRepo.get('location');

    const user = getData('user');
    const branchId = user.branchId || 1;

    let selectedFilterLocation = 0;
    if (location) {
      const type = location?.split('x')[0];
      if (type === 'classroom') {
        selectedFilterLocation = 2;
      }
      if (type === 'yard') {
        selectedFilterLocation = 1;
      }
    }

    const selectedBranch = location ? location : `allxallx${branchId}`;
    return { selectedBranch, selectedFilterLocation };
  };
  const { selectedBranch, selectedFilterLocation } = getInitialLocation();

  const [student, setStudent] = useState('');
  const [location, setLocation] = useState(selectedBranch);
  const [locationFilter, setLocationFilter] = useState(selectedFilterLocation);
  const [locationType, locationId, branchId] = (location || '')?.split('x');

  const [filterParams, setFilterParams] = useState({
    branchId,
    locationType: locationFilter,
    ...(student.length > 0 && { student: student })
  });

  const allLocations = getData('locations');

  const locations = {
    options: [{ id: 'all', label: 'All Branches', type: 0 }],
    ...attendanceConfig.location
  };

  const debounceCallback = useCallback(
    debounce(
      params => {
        setFilterParams(params);
      },
      1000,
      false
    ),
    []
  );

  useEffect(() => {
    if (allLocations && location) {
      if (!allLocations.find(({ id }) => id === location) && location !== 'all') {
        onFilterBranch(allLocations[0].id);
      }
    }
  }, []);

  const locationFilterOptions = useMemo(() => {
    const branchLocations = (allLocations || []).filter(l => `${l.branchId}` === `${branchId}`);
    const branchTypes = branchLocations.map(l => l.type);
    return LOCATION_FILTERS.map(l => ({ ...l, disabled: !branchTypes.includes(l.id) }));
  }, [branchId, allLocations]);

  const onFilterBranch = (loc, resetLocationFilter) => {
    setLocation(loc);
    resetLocationFilter && setLocationFilter(0);

    const isAllBranches = loc === 'all';
    if (isAllBranches) {
      PersistentRepo.set('allBranches', loc);
    } else {
      allBranchesLocation && PersistentRepo.delete('allBranches');
    }

    let selectedFilterLocation = 0;
    if (loc) {
      const type = loc?.split('x')[0];
      if (type === 'classroom') {
        selectedFilterLocation = 2;
      }
      if (type === 'yard') {
        selectedFilterLocation = 1;
      }
    }

    setFilterParams(prevState => {
      return {
        ...prevState,
        branchId: isAllBranches ? undefined : loc.split('x')[2],
        locationType: isAllBranches ? 0 : selectedFilterLocation
      };
    });
  };

  const onFilterByLocation = val => {
    setLocationFilter(val);
    const selectedLocation = allLocations.find(
      l => `${l.branchId}` === location.split('x')[2] && l.type === val
    ).id;
    onFilterBranch(selectedLocation, false);
  };

  const downloadIntervention = async id => {
    try {
      const response = await fetch(`${baseApi}/student_intervention/${id}`, {
        method: 'POST',
        body: JSON.stringify({}),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const fileBlob = await response.blob();
      download(fileBlob, 'intervention.pdf');
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <PageContainer style={stylesTablet}>
      <Card title="Student Intervention">
        <FilterRow>
          <Select
            showSearch
            onChange={onFilterBranch}
            {...locations}
            value={branchId !== undefined ? `allxallx${branchId}` : 'all'}
            label="Branch"
            filterFn={item => item.type === 0}
            style={{ width: '250px' }}
          />

          <Select
            onChange={onFilterByLocation}
            value={filterParams.locationType}
            options={locationFilterOptions}
            label="Filter by Type"
            disabled={branchId === undefined}
            style={{ width: '200px' }}
          />

          <Input
            label="Full Name"
            onChange={e => {
              setStudent(e);
              debounceCallback({ ...filterParams, student: e });
            }}
            value={student}
          />
        </FilterRow>

        <AsyncTable
          pagination
          fileName={resource}
          resource={resource}
          config={{
            ...config,
            table: [
              ...config.table,
              {
                id: 'download',
                render: interventionRow => {
                  return (
                    <Button
                      type="primary"
                      onClick={() => {
                        downloadIntervention(interventionRow.interventionId);
                      }}
                      // style={{ width: '200px' }}
                    >
                      Download Intervention
                    </Button>
                  );
                }
              }
            ]
          }}
          params={{
            ...filterParams
          }}
          onChange={(pagination, filters, sorter) => {
            console.log(sorter.field);
            if (sorter.field) {
              setFilterParams({
                ...filterParams,
                ...(sorter.field && {
                  orderBy: camelToSnakeCase(
                    sorter.field === 'firstName'
                      ? 'firstName'
                      : sorter.field == 'lastName'
                      ? 'lastName'
                      : sorter.field === 'submitDate'
                      ? 'interventionCreatedAt'
                      : 'branchName'
                  ),
                  order: sorter.order === 'ascend' ? 'asc' : 'desc'
                })
              });
            }
          }}
        />
      </Card>
    </PageContainer>
  );
};
